import { useState, useEffect } from 'react'
import './SingleEditModal.css'
import { Device } from '@/models/device'
import { Configuration, PartnerConfigurationService } from '@/services/PartnerConfigurationService'
import { PartnerSimProviderService } from '@/services/PartnerSimProviderService'
import { SimProvider } from '@/models/simProvider'
import { PartnerFirmwareVersionService } from '@/services/PartnerFirmwareVersionService'
import { PartnerCustomFieldService } from '@/services/PartnerCustomFieldService'
import { CustomField, SubCustomField } from '@/models/customField'

interface SingleEditModalProps {
  device: Device,
  devices: Device[],
  canEditDeviceIdentity: boolean,
  onClose: () => void
  onSave: (deviceUuid: string, updates: Partial<Device>) => void
}

interface FieldState {
  value: string
  applied: boolean
  error?: string
}

export default function SingleEditModal({ device, devices, canEditDeviceIdentity, onClose, onSave }: SingleEditModalProps) {
  const [fieldStates, setFieldStates] = useState<Record<string, FieldState>>({})
  const [subCustomFieldStates, setSubCustomFieldStates] = useState<Record<string, FieldState>>({})
  const [pendingUpdates, setPendingUpdates] = useState<Partial<Device>>({})
  const [configurations, setConfigurations] = useState<Configuration[]>([])
  const [simProviders, setSimProviders] = useState<SimProvider[]>([])
  const [customFields, setCustomFields] = useState<CustomField[]>([])
  const [firmwareVersions, setFirmwareVersions] = useState<string[]>([])
  const [loadingData, setLoadingData] = useState(false)

  useEffect(() => {
    // Initialize form with current device data
    setFieldStates({
      serialNumber: { value: device.serialNumber, applied: false },
      imei: { value: device.imei || '', applied: false },
      type: { value: device.type || '', applied: false },
      phoneNumber: { value: device.phoneNumber || '', applied: false },
      simSerialNumber: { value: device.simSerialNumber || '', applied: false },
      simProviderId: { value: device.simProviderId?.toString() || '', applied: false },
      // customFieldId: { value: device.customFieldId?.toString() || '', applied: false },
      firmware: { value: device.firmware || '', applied: false },
      configurationId: { value: device.configurationId || '', applied: false }
    })
  }, [device])

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingData(true)
        const [configurationData, customFieldData, simProvidersData, firmwareVersionsData] = await Promise.all([
          PartnerConfigurationService.getAll(),
          PartnerCustomFieldService.getAll(),
          PartnerSimProviderService.getAll(),
          PartnerFirmwareVersionService.getAll()
        ])

        setConfigurations(configurationData.configurations)
        setCustomFields(customFieldData.customFields)
        setSimProviders(simProvidersData.simProviders)
        setFirmwareVersions(firmwareVersionsData.versions)

      } catch (error) {
        console.error(error)
      } finally {
        setLoadingData(false)
      }
    }

    fetchData()
  }, [])

  const validateDuplicates = (field: keyof Device, value: string): string | undefined => {
    if (!value || value === 'blank') return undefined

    // Check for duplicates in other devices (excluding current device)
    const uniqueFields = ['serialNumber', 'imei', 'phoneNumber', 'simSerialNumber']
    if (uniqueFields.includes(field)) {
      const duplicateCount = devices.filter(d =>
        d.id !== device.id && d[field] === value && d[field] !== ''
      ).length

      if (duplicateCount > 0) {
        return `Duplicate ${field} found. This field must be unique.`
      }
    }

    // Additional validation
    if (field === 'serialNumber' && !value.trim()) {
      return 'Serial Number is required'
    }

    if (field === 'phoneNumber' && value && !/^\+?[\d\s-()]+$/.test(value)) {
      return 'Phone number format is invalid'
    }

    if (field === 'imei' && value && !/^\d{15}$/.test(value.replace(/\s/g, ''))) {
      return 'IMEI must be 15 digits'
    }

    return undefined
  }

  const handleFieldChange = (field: string, value: string) => {
    const error = validateDuplicates(field as keyof Device, value)
    setFieldStates(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        value,
        error,
        applied: false // Reset applied state when value changes
      }
    }))
  }

  const handleSubCustomFieldChange = (fieldId: string, value: string) => {
    setSubCustomFieldStates(prev => ({
      ...prev,
      [fieldId]: {
        ...prev[fieldId],
        value,
        applied: false // Reset applied state when value changes
      }
    }))
  }

  const handleApplyField = (field: string) => {
    const fieldState = fieldStates[field]
    if (fieldState.error) return

    let value: any = fieldState.value === 'blank' ? '' : fieldState.value

    setFieldStates(prev => ({
      ...prev,
      [field]: { ...prev[field], applied: true }
    }))

    setPendingUpdates(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleApplySubCustomField = (fieldId: string) => {
    const fieldState = subCustomFieldStates[fieldId]
    if (fieldState.error) return

    let value: any = fieldState.value === 'blank' ? '' : fieldState.value

    setSubCustomFieldStates(prev => ({
      ...prev,
      [fieldId]: { ...prev[fieldId], applied: true }
    }))

    setPendingUpdates(prev => ({
      ...prev,
      [fieldId]: value
    }))
  }

  const handleApplyAllUpdates = (e: React.FormEvent) => {
    e.preventDefault()

    const allSubFields = customFields?.flatMap(cf => cf.subFields ?? []);

    // Check if any fields have errors
    const hasErrors = Object.values(fieldStates).some(state => state.error)
    if (hasErrors) return

    // Only include applied fields
    const appliedUpdates: Partial<Device> = {}
    Object.entries(fieldStates).forEach(([field, state]) => {
      if (state.applied) {
        let value: any = state.value === 'blank' ? '' : state.value

        appliedUpdates[field as keyof Device] = value as any
      }
    })
    
    const subCustomFields: string[] = [];
    Object.entries(subCustomFieldStates).forEach(([field, state]) => {
      if (state.applied) {
        let value: any = state.value === 'blank' ? '' : state.value

        subCustomFields.push(value); 
      }
    })

    if (subCustomFields.length > 0) {
      const subCustomFieldItems: SubCustomField[] = subCustomFields.map(scf => {
        const subFieldMeta = allSubFields.find(field => field?.id === scf)!;
        return {
          id: scf,
          name: subFieldMeta.name,
          description: subFieldMeta.description,
          createdAtUtc: subFieldMeta.createdAtUtc,
          isDefault: subFieldMeta.isDefault,
          parentCustomFieldId: subFieldMeta.parentCustomFieldId
        };
      }) || [];

      appliedUpdates.subCustomFields = subCustomFieldItems
    }
    
    onSave(device.id, appliedUpdates)
  }

  const getAppliedCount = () => {
    return Object.values(fieldStates).filter(state => state.applied).length + Object.values(subCustomFieldStates).filter(state => state.applied).length
  }

  return (
    <div className="modal-overlay">
  <div className="single-edit-modal-content">
    <div className="modal-header">
      <h2>Edit Device</h2>
      <p>Editing device: {device.serialNumber}</p>
      <button className="modal-close" onClick={onClose}>×</button>
    </div>

    <form onSubmit={handleApplyAllUpdates} className="single-edit-form">
      <div className="applied-summary">
        {getAppliedCount() > 0 && (
          <div className="applied-count">
            {getAppliedCount()} field{getAppliedCount() > 1 ? 's' : ''} applied and ready for update
          </div>
        )}
      </div>

      <div className="form-row">
        <div className="form-field-group">
          <label htmlFor="serialNumber">Device Serial Number *</label>
          <div className="field-with-apply">
            <input
              type="text"
              id="serialNumber"
              value={fieldStates.serialNumber?.value || ''}
              disabled={!canEditDeviceIdentity}
              onChange={(e) => handleFieldChange('serialNumber', e.target.value)}
              className={fieldStates.serialNumber?.error ? 'error' : ''}
            />
            {canEditDeviceIdentity && (
              <button
                type="button"
                className={`btn-apply ${fieldStates.serialNumber?.applied ? 'applied' : ''}`}
                onClick={() => handleApplyField('serialNumber')}
                disabled={!fieldStates.serialNumber?.value || !!fieldStates.serialNumber?.error}
              >
                {fieldStates.serialNumber?.applied ? 'Applied' : 'Apply'}
              </button>
            )}
          </div>
          {fieldStates.serialNumber?.error && <span className="error-text">{fieldStates.serialNumber.error}</span>}
        </div>

        <div className="form-field-group">
          <label htmlFor="imei">IMEI Number</label>
          <div className="field-with-apply">
            <input
              type="text"
              id="imei"
              value={fieldStates.imei?.value || ''}
              onChange={(e) => handleFieldChange('imei', e.target.value)}
              disabled={!canEditDeviceIdentity}
              placeholder="15-digit IMEI number"
              className={fieldStates.imei?.error ? 'error' : ''}
            />
            {canEditDeviceIdentity && (
              <button
                type="button"
                className={`btn-apply ${fieldStates.imei?.applied ? 'applied' : ''}`}
                onClick={() => handleApplyField('imei')}
                disabled={!fieldStates.imei?.value || !!fieldStates.imei?.error}
              >
                {fieldStates.imei?.applied ? 'Applied' : 'Apply'}
              </button>
            )}
          </div>
          {fieldStates.imei?.error && <span className="error-text">{fieldStates.imei.error}</span>}
        </div>
      </div>

      <div className="form-row">
        <div className="form-field-group">
          <label htmlFor="type">Device Type</label>
          <div className="field-with-apply">
            <select
              id="type"
              value={fieldStates.type?.value || ''}
              onChange={(e) => handleFieldChange('type', e.target.value)}
              disabled={!canEditDeviceIdentity}
              className={fieldStates.type?.error ? 'error' : ''}
            >
              <option value="">Select device type</option>
              <option value="ID Touch">ID Touch</option>
              <option value="Shield">Shield</option>
              <option value="Curve">Curve</option>
            </select>
            {canEditDeviceIdentity && (
              <button
                type="button"
                className={`btn-apply ${fieldStates.type?.applied ? 'applied' : ''}`}
                onClick={() => handleApplyField('type')}
                disabled={!canEditDeviceIdentity || !fieldStates.type?.value || !!fieldStates.type?.error}
              >
                {fieldStates.type?.applied ? 'Applied' : 'Apply'}
              </button>
            )}
          </div>
          {fieldStates.type?.error && <span className="error-text">{fieldStates.type.error}</span>}
        </div>

        <div className="form-field-group">
          <label htmlFor="phoneNumber">Phone Number</label>
          <div className="field-with-apply">
            <input
              type="tel"
              id="phoneNumber"
              value={fieldStates.phoneNumber?.value || ''}
              onChange={(e) => handleFieldChange('phoneNumber', e.target.value)}
              placeholder="+44 7000 000 000"
              className={fieldStates.phoneNumber?.error ? 'error' : ''}
            />
            <button
              type="button"
              className={`btn-apply ${fieldStates.phoneNumber?.applied ? 'applied' : ''}`}
              onClick={() => handleApplyField('phoneNumber')}
              disabled={!fieldStates.phoneNumber?.value || !!fieldStates.phoneNumber?.error}
            >
              {fieldStates.phoneNumber?.applied ? 'Applied' : 'Apply'}
            </button>
          </div>
          {fieldStates.phoneNumber?.error && <span className="error-text">{fieldStates.phoneNumber.error}</span>}
        </div>
      </div>

      <div className="form-row">
        <div className="form-field-group">
          <label htmlFor="simSerialNumber">SIM Serial Number</label>
          <div className="field-with-apply">
            <input
              type="text"
              id="simSerialNumber"
              value={fieldStates.simSerialNumber?.value || ''}
              onChange={(e) => handleFieldChange('simSerialNumber', e.target.value)}
              placeholder="SIM card serial number"
              className={fieldStates.simSerialNumber?.error ? 'error' : ''}
            />
            <button
              type="button"
              className={`btn-apply ${fieldStates.simSerialNumber?.applied ? 'applied' : ''}`}
              onClick={() => handleApplyField('simSerialNumber')}
              disabled={!fieldStates.simSerialNumber?.value || !!fieldStates.simSerialNumber?.error}
            >
              {fieldStates.simSerialNumber?.applied ? 'Applied' : 'Apply'}
            </button>
          </div>
          {fieldStates.simSerialNumber?.error && <span className="error-text">{fieldStates.simSerialNumber.error}</span>}
        </div>

        <div className="form-field-group">
          <label htmlFor="simProviderId">SIM Provider</label>
          <div className="field-with-apply">
            <select
              id="simProviderId"
              value={fieldStates.simProviderId?.value || ''}
              onChange={(e) => handleFieldChange('simProviderId', e.target.value)}
              className={fieldStates.simProviderId?.error ? 'error' : ''}
              disabled={loadingData}
            >
              <option value="">
                {loadingData ? 'Loading...' : 'Select SIM provider'}
              </option>
              {simProviders.map((provider) => (
                <option key={provider.id} value={provider.id.toString()}>
                  {provider.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              className={`btn-apply ${fieldStates.simProviderId?.applied ? 'applied' : ''}`}
              onClick={() => handleApplyField('simProviderId')}
              disabled={loadingData || !fieldStates.simProviderId?.value || !!fieldStates.simProviderId?.error}
            >
              {fieldStates.simProviderId?.applied ? 'Applied' : 'Apply'}
            </button>
          </div>
          {fieldStates.simProviderId?.error && <span className="error-text">{fieldStates.simProviderId.error}</span>}
        </div>
      </div>

      <div className="form-row">
        <div className="form-field-group">
          <label htmlFor="firmware">Firmware</label>
          <div className="field-with-apply">
            <select
              id="firmware"
              value={fieldStates.firmware?.value || ''}
              onChange={(e) => handleFieldChange('firmware', e.target.value)}
              className={fieldStates.firmware?.error ? 'error' : ''}
              disabled={loadingData}
            >
              <option value="">
                {loadingData ? 'Loading...' : 'Select firmware version'}
              </option>
              {firmwareVersions?.map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
            <button
              type="button"
              className={`btn-apply ${fieldStates.firmware?.applied ? 'applied' : ''}`}
              onClick={() => handleApplyField('firmware')}
              disabled={loadingData || !fieldStates.firmware?.value || !!fieldStates.firmware?.error}
            >
              {fieldStates.firmware?.applied ? 'Applied' : 'Apply'}
            </button>
          </div>
          {fieldStates.firmware?.error && <span className="error-text">{fieldStates.firmware.error}</span>}
        </div>

        <div className="form-field-group">
          <label htmlFor="configurationId">Configuration</label>
          <div className="field-with-apply">
            <select
              id="configurationId"
              value={fieldStates.configurationId?.value || ''}
              onChange={(e) => handleFieldChange('configurationId', e.target.value)}
              className={fieldStates.configurationId?.error ? 'error' : ''}
              disabled={loadingData}
            >
              <option value="">
                {loadingData ? 'Loading...' : 'Select configuration'}
              </option>
              {configurations.map((config) => (
                <option key={config.id} value={config.id}>{config.name}</option>
              ))}
            </select>
            <button
              type="button"
              className={`btn-apply ${fieldStates.configurationId?.applied ? 'applied' : ''}`}
              onClick={() => handleApplyField('configurationId')}
              disabled={loadingData || !fieldStates.configurationId?.value || !!fieldStates.configurationId?.error}
            >
              {fieldStates.configurationId?.applied ? 'Applied' : 'Apply'}
            </button>
          </div>
          {fieldStates.configurationId?.error && <span className="error-text">{fieldStates.configurationId.error}</span>}
        </div>
      </div>

      {customFields?.reduce((rows: CustomField[][], field, index) => {
        if (index % 2 === 0) rows.push([field]);
        else rows[rows.length - 1].push(field);
        return rows;
      }, []).map((row, rowIndex) => (
        <div className="form-row" key={`custom-row-${rowIndex}`}>
          {row.map((field) => (
            <div className="form-field-group" key={field.id}>
              <label htmlFor={field.id}>{field.name}</label>
              <div className="field-with-apply">
                <select
                    id={field.id}
                    value={subCustomFieldStates[field.id]?.value || ''}
                    onChange={(e) => handleSubCustomFieldChange(field.id, e.target.value)}
                    className={subCustomFieldStates[field.id]?.error ? 'error' : ''}
                  >
                    <option value="">Select custom field</option>
                    {field.subFields?.map(opt => (
                      <option value={opt.id}>{opt.name}</option>
                    ))}
                  </select>
                <button
                  type="button"
                  className={`btn-apply ${subCustomFieldStates[field.id]?.applied ? 'applied' : ''}`}
                  onClick={() => handleApplySubCustomField(field.id)}
                  disabled={!subCustomFieldStates[field.id]?.value || !!subCustomFieldStates[field.id]?.error}
                >
                  {subCustomFieldStates[field.id]?.applied ? 'Applied' : 'Apply'}
                </button>
              </div>
              {subCustomFieldStates[field.id]?.error && <span className="error-text">{subCustomFieldStates[field.id].error}</span>}
            </div>
          ))}
          {row.length === 1 && <div className="form-field-group" style={{ visibility: 'hidden' }}></div>}
        </div>
      ))}

      <div className="form-info">
        <div className="info-item">
          <strong>Date Added:</strong> {new Date(device.dateAddedUtc).toLocaleDateString()}
        </div>
      </div>

      <div className="form-actions">
        <button type="button" className="btn-cancel" onClick={onClose}>
          Cancel
        </button>
        <button
          type="submit"
          className="btn-apply-all"
          disabled={getAppliedCount() === 0}
        >
          Apply All Updates ({getAppliedCount()} field{getAppliedCount() !== 1 ? 's' : ''})
        </button>
      </div>
    </form>
  </div>
</div>
  )
}